import React, { useEffect, useState } from "react";
import {
  createStyles,
  Theme,
  WithStyles,
  Typography,
  Button,
  Box,
  CircularProgress,
} from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
import { HighlightOff } from "@material-ui/icons";
import { connect } from "react-redux";
import paymentApi from "../../api/payment";

interface Props extends WithStyles<typeof styles> {
  sessionId?: string;
  history?: any;
}

const StripePaymentCancelled: React.FC<Props> = ({ classes, history, sessionId }) => {
  const [releasing, setReleasing] = useState(!!sessionId);
  const [releaseError, setReleaseError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setReleasing(false);
      return;
    }

    paymentApi
      .cancelStripeSession(sessionId)
      .catch((err: any) => {
        const message = err?.response?.data?.error || "Could not release the payment slot. You can try paying again.";
        setReleaseError(message);
      })
      .finally(() => setReleasing(false));
  }, [sessionId]);

  return (
    <Box className={classes.page}>
      <Box className={classes.card}>
        <Box className={classes.iconWrap}>
          <HighlightOff className={classes.icon} />
        </Box>
        <Typography variant="h5" component="h1" className={classes.title}>
          Payment Cancelled
        </Typography>
        <Typography variant="body1" className={classes.body} color="textSecondary">
          No charge was made. You can try again when you&apos;re ready to pay.
        </Typography>
        <Typography variant="body2" className={classes.footer} color="textSecondary">
          Your card was not charged.
        </Typography>
        {releasing && (
          <Box className={classes.statusRow}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="textSecondary">
              Releasing payment slot…
            </Typography>
          </Box>
        )}
        {!releasing && releaseError && (
          <Typography variant="body2" className={classes.releaseError} color="error">
            {releaseError}
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={() => (history ? history.replace("/") : (window.location.href = "/"))}
          className={classes.button}
          fullWidth
          size="large"
          disabled={releasing}
        >
          Back to Dashboard
        </Button>
      </Box>
    </Box>
  );
};

const styles = (theme: Theme) =>
  createStyles({
    page: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "calc(100vh - 56px)",
      padding: theme.spacing(2),
      boxSizing: "border-box",
      width: "100%",
    },
    card: {
      width: "100%",
      maxWidth: 420,
      padding: theme.spacing(4),
      backgroundColor: theme.palette.background.paper,
      boxShadow: "0 8px 40px rgba(140, 100, 60, 0.12)",
      borderRadius: 16,
      textAlign: "center",
      border: "1px solid rgba(140, 100, 60, 0.2)",
      animation: "$slideUp 0.4s ease-out",
      [theme.breakpoints.up("sm")]: {
        padding: theme.spacing(5),
      },
    },
    "@keyframes slideUp": {
      from: {
        opacity: 0,
        transform: "translateY(16px)",
      },
      to: {
        opacity: 1,
        transform: "translateY(0)",
      },
    },
    iconWrap: {
      display: "flex",
      justifyContent: "center",
      marginBottom: theme.spacing(3),
    },
    icon: {
      fontSize: 72,
      color: "#b8860b",
      opacity: 0.9,
      filter: "drop-shadow(0 4px 12px rgba(184, 134, 11, 0.2))",
    },
    title: {
      fontWeight: 700,
      color: "#8b6914",
      marginBottom: theme.spacing(2),
      letterSpacing: "-0.02em",
    },
    body: {
      marginBottom: theme.spacing(2),
      lineHeight: 1.6,
    },
    footer: {
      marginBottom: theme.spacing(3),
    },
    statusRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
    },
    releaseError: {
      marginBottom: theme.spacing(2),
      lineHeight: 1.5,
    },
    button: {
      marginTop: theme.spacing(1),
      padding: theme.spacing(1.5, 3),
      borderRadius: 12,
      fontWeight: 700,
      textTransform: "none",
      fontSize: "1rem",
    },
  });

const mapStateToProps = (_state: any, ownProps: any) => {
  const params = new URLSearchParams(ownProps.location?.search || "");
  return {
    sessionId: params.get("session_id") || undefined,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(StripePaymentCancelled));
